import { GridSortItem } from "@mui/x-data-grid";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  FeeSchedule,
  FeeScheduleControllerApi,
  GetFeeSchedulesFeeScheduleTypeEnum,
} from "../../services";
import { Identified } from "../../types/sdk-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  useBaseContent,
} from "../../utils/utils";

/**
 * A row from `getFeeSchedules`. The rows carry `firstName` / `lastName`, which
 * the generated `FeeSchedule` does not declare — see the PR's latent-bug list.
 */
export type FeeScheduleListItem = Identified<FeeSchedule> & {
  firstName?: string;
  lastName?: string;
};

interface UseFeeScheduleListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  providerUuid?: string[];
  medicalCodeUuid?: string;
  status?: string | null;
  archive?: boolean;
  feeScheduleType?: GetFeeSchedulesFeeScheduleTypeEnum;
  payerId?: string[];
  searchString?: string;
  xTENANTID?: string;
}

/**
 *
 * @author Rajkumar Rathod
 */
export function useFeeScheduleList({
  pagination,
  gridSorting,
  providerUuid,
  medicalCodeUuid,
  status,
  archive,
  feeScheduleType,
  payerId,
  searchString,
  xTENANTID,
}: UseFeeScheduleListProps): useBaseContent<FeeScheduleListItem> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey: searchString,
    prevPage,
  });
  const pageSize = getPageSize(searchString, pagination);

  const sortData = gridSorting?.[0];

  return useQuery({
    queryKey: [
      "fee-schedule-list",
      pageSize,
      pageIndex,
      providerUuid,
      medicalCodeUuid,
      status,
      feeScheduleType,
      payerId,
      archive,
      searchString,
      xTENANTID,
      gridSorting,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      // page?: number, size?: number, sortBy?: string, sort?: string, providerUuid?: string, medicalCodeUuid?: string, status?: boolean, archive?: boolean, searchString?: string, xTENANTID?: string, options?: RawAxiosRequestConfig
      new FeeScheduleControllerApi(getConfigObject())
        .getFeeSchedules(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sort
          providerUuid?.length ? providerUuid : undefined,
          medicalCodeUuid,
          // The filter holds the select's string value; it has always been
          // forwarded verbatim to a param the SDK declares `boolean`.
          status as unknown as boolean,
          archive,
          feeScheduleType,
          payerId?.length ? payerId : undefined,
          searchString, //searchString
          xTENANTID //xTENANTID
        )
        .then((res) => asPaginatedContent<FeeScheduleListItem>(res?.data)),
  });
}
